import api from './baseApi';
import { contentLanguage, langParams } from 'src/i18n/contentLanguage';

/**
 * The programme of a LONELINESS Circle
 * (docs/briefs/dunbar148-loneliness-plan.md section 5.2): eight weeks with a
 * meeting theme and a step, the leader moving the group week by week, the
 * members checking in on each step. Every route answers 404 for a Circle
 * without the LONELINESS code, and 404 for a Circle that has not started.
 */

export type AssessmentWindowRef = 'BEFORE' | 'MID' | 'AFTER' | 'FOLLOW_UP';

export type ProgrammeCheckinAnswer = 'YES' | 'PARTLY' | 'NO';

export type CircleProgrammeStatus = 'ACTIVE' | 'COMPLETED';

export interface ProgrammeTemplateListItem {
  key: string;
  version: number;
  language: string;
  special_code: string;
  title: string;
  description: string;
  weeks_total: number;
}

export interface ProgrammeWeek {
  number: number;
  title: string;
  meeting_text: string;
  step_text: string;
  assessment_window: AssessmentWindowRef | null;
}

export interface ProgrammeOwnCheckin {
  week: number;
  answer: ProgrammeCheckinAnswer;
  note: string | null;
  updated_at?: string;
}

export interface CircleProgramme {
  template: { key: string; version: number };
  /** The language the texts are in. */
  language?: string;
  title: string;
  status: CircleProgrammeStatus;
  started_at: string;
  current_week: number;
  weeks_total: number;
  weeks: ProgrammeWeek[];
  /** The caller's own check-ins; empty for leaders. */
  my_checkins: ProgrammeOwnCheckin[];
  /** Members check in; leaders and co-leaders run the Circle and do not. */
  can_check_in: boolean;
  /** Leaders only; null for members. */
  leader_guide: string | null;
}

export interface ProgrammeWeekCheckinSummary {
  week: number;
  title: string;
  step_text: string;
  members: number;
  answered: number;
  yes: number;
  partly: number;
  no: number;
}

export interface ProgrammeCheckinMember {
  user_id: string;
  full_name: string | null;
  /** Answer per week, keyed by the week number; a missing week is unanswered. */
  checkins: Record<
    string,
    { answer: ProgrammeCheckinAnswer; note: string | null }
  >;
}

export interface ProgrammeCheckins {
  current_week: number;
  weeks: ProgrammeWeekCheckinSummary[];
  members: ProgrammeCheckinMember[];
}

export interface ProgrammeStart {
  /** The language of the Circle's copy; the browser's unless given. */
  language?: 'en' | 'nb';
  template_key?: string;
  template_version?: number;
  started_at?: string;
}

export interface ProgrammeUpdate {
  current_week?: number;
  status?: CircleProgrammeStatus;
  started_at?: string;
}

type ApiEnvelope<T> = { result: T };

const circlePath = (circleId: string): string =>
  `tribes/${encodeURIComponent(circleId)}/programme`;

/** The API answers 404 both for a Circle without the code and one not started. */
export const isNotFound = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'response' in error &&
  (error as { response?: { status?: number } }).response?.status === 404;

export const programmesApi = {
  listTemplates: async (
    specialCode?: string
  ): Promise<ProgrammeTemplateListItem[]> => {
    const { data } = await api.get<ApiEnvelope<ProgrammeTemplateListItem[]>>(
      'programme-templates',
      {
        params: {
          ...langParams(),
          ...(specialCode ? { special_code: specialCode } : {}),
        },
      }
    );
    return data.result;
  },

  get: async (circleId: string): Promise<CircleProgramme> => {
    const { data } = await api.get<ApiEnvelope<CircleProgramme>>(
      circlePath(circleId),
      { params: langParams() }
    );
    return data.result;
  },

  /** A leader starts the Circle from a template; once per Circle. */
  start: async (
    circleId: string,
    body: ProgrammeStart = {}
  ): Promise<CircleProgramme> => {
    const { data } = await api.post<ApiEnvelope<CircleProgramme>>(
      circlePath(circleId),
      { language: contentLanguage(), ...body },
      { params: langParams() }
    );
    return data.result;
  },

  /** A leader moves the group to a week, or closes the programme. */
  update: async (
    circleId: string,
    body: ProgrammeUpdate
  ): Promise<CircleProgramme> => {
    const { data } = await api.patch<ApiEnvelope<CircleProgramme>>(
      circlePath(circleId),
      body,
      { params: langParams() }
    );
    return data.result;
  },

  /** A member's answer for a week; changeable. */
  checkIn: async (
    circleId: string,
    body: { week: number; answer: ProgrammeCheckinAnswer; note?: string }
  ): Promise<ProgrammeOwnCheckin> => {
    const { data } = await api.put<ApiEnvelope<ProgrammeOwnCheckin>>(
      `${circlePath(circleId)}/checkins`,
      body
    );
    return data.result;
  },

  /** For the Circle's leaders: every week's counts and every member's answers. */
  getCheckins: async (circleId: string): Promise<ProgrammeCheckins> => {
    const { data } = await api.get<ApiEnvelope<ProgrammeCheckins>>(
      `${circlePath(circleId)}/checkins`,
      { params: langParams() }
    );
    return data.result;
  },
};
